/**
 * Fairway Crooks - AJAX Cart Handler
 * Handles cart interactions, quantity updates, and side-cart rendering
 */

class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('CartDrawer');
    this.content = document.getElementById('CartDrawerContent');
    this.cartCount = document.querySelectorAll('[data-cart-count]');
    this.init();
  }

  init() {
    if (!this.drawer) return;

    // Listen for custom "cart:open" event (useful for header/product quick-add)
    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:refresh', () => this.refresh());

    // Listen for add-to-cart form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.action.includes('/cart/add')) {
        e.preventDefault();
        this.addToCart(form);
      }
    });

    // Delegate clicks for quantity changes and removal
    this.drawer.addEventListener('click', (e) => {
      const qtyBtn = e.target.closest('[data-cart-quantity-change]');
      const removeBtn = e.target.closest('[data-cart-remove]');
      
      if (qtyBtn) {
        const itemKey = qtyBtn.closest('[data-cart-item]').dataset.cartItem;
        const newQty = parseInt(qtyBtn.dataset.qty);
        this.updateQuantity(itemKey, newQty);
      }

      if (removeBtn) {
        const itemKey = removeBtn.closest('[data-cart-item]').dataset.cartItem;
        this.updateQuantity(itemKey, 0);
      }
    });

    // Add listener for Quick Add buttons across the site
    document.addEventListener('click', (e) => {
      const quickAddBtn = e.target.closest('[data-quick-add]');
      if (quickAddBtn) {
        e.preventDefault();
        const variantId = quickAddBtn.dataset.quickAdd;
        this.addVariantById(variantId);
      }
    });
  }

  open() {
    if (this.drawer) {
      this.drawer.setAttribute('data-open', 'true');
      document.body.style.overflow = 'hidden';
    }
  }

  close() {
    if (this.drawer) {
      this.drawer.setAttribute('data-open', 'false');
      document.body.style.overflow = '';
    }
  }

  async addToCart(form) {
    const submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    try {
      const formData = new FormData(form);
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Cart add failed');

      await this.refresh();
      this.open();
    } catch (error) {
      console.error('Add to cart error:', error);
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  async addVariantById(variantId, quantity = 1) {
    this.setLoading(true);
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Quick add failed');

      await this.refresh();
      this.open();
    } catch (error) {
      console.error('Quick add error:', error);
    } finally {
      this.setLoading(false);
    }
  }

  async updateQuantity(key, quantity) {
    this.setLoading(true);
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });

      if (!response.ok) throw new Error('Update failed');
      
      await this.refresh();
    } catch (error) {
      console.error('Update quantity error:', error);
    } finally {
      this.setLoading(false);
    }
  }

  async refresh() {
    try {
      // Use Section Rendering API to get the updated cart-drawer section
      const response = await fetch(`${window.Shopify.routes.root}?sections=cart-drawer`);
      const data = await response.json();
      const html = data['cart-drawer'];
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const newContent = doc.getElementById('CartDrawerContent');
      const newHeader = doc.querySelector('#CartDrawer header');
      
      if (newContent && this.content) {
        this.content.innerHTML = newContent.innerHTML;
      }
      
      if (newHeader) {
        const currentHeader = this.drawer.querySelector('header');
        if (currentHeader) currentHeader.innerHTML = newHeader.innerHTML;
      }

      // Parse item count from the freshly rendered cart drawer HTML
      const countEl = doc.querySelector('[data-cart-count]');
      const itemCount = countEl ? parseInt(countEl.textContent.trim(), 10) || 0 : 0;

      this.cartCount.forEach((el) => {
        el.textContent = itemCount;
        if (itemCount > 0) {
          el.classList.remove('scale-0');
          el.classList.add('scale-100');
        } else {
          el.classList.add('scale-0');
          el.classList.remove('scale-100');
        }
      });

    } catch (error) {
      console.error('Refresh cart error:', error);
    }
  }

  setLoading(isLoading) {
    if (this.drawer) {
      this.drawer.style.opacity = isLoading ? '0.6' : '1';
      this.drawer.style.pointerEvents = isLoading ? 'none' : 'auto';
    }
  }
}

// Initialize on DOM load
document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawer();
});
